import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import {
  STRINGS,
  PLATFORM_GUIDES,
  aiImprovementEnabledClient,
  type BusinessFacts,
  type ImprovementAction,
  type WebsitePlatform,
} from '@maintenance-tracker/shared';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { api, ApiError, cn } from '@/lib/utils';
import {
  ArrowLeft,
  Check,
  Copy,
  Download,
  FileText,
  Loader2,
  Mail,
  Monitor,
  Phone,
  RotateCcw,
  Sparkles,
  Wrench,
} from 'lucide-react';

type WizardStep = 'home' | 'facts' | 'edit' | 'export';

type HomeData = {
  recommended: Array<{ action: ImprovementAction; findingId: string; title: string; why: string | null }>;
  findingCounts: Record<string, number>;
  statusByAction: Record<string, string>;
  outputs: Array<{ id: string; actionType: string; status: string; updatedAt: string; version: number }>;
  latestReviewId: string | null;
  aiEnabled: boolean;
};

type OutputDetail = {
  output: {
    id: string;
    actionType: string;
    findingId: string | null;
    factsSnapshot: BusinessFacts;
    content: {
      title?: string;
      plainText?: string;
      fields?: Record<string, string>;
      placeholders?: string[];
      warnings?: string[];
      suggestedPlainText?: string;
      suggestedHtml?: string;
      jsonLd?: string | null;
    };
    html: string | null;
    version: number;
    status: string;
    placeholdersOpen: number;
  };
  revisions: Array<{ id: string; revision: number; content: Record<string, unknown>; createdAt: string }>;
  aiEnabled: boolean;
};

const ACTIONS: Array<{
  id: ImprovementAction;
  title: string;
  outcome: string;
}> = [
  { id: 'service_page', title: 'Improve my service page', outcome: 'Clear service copy customers can understand' },
  { id: 'trust_section', title: 'Improve my trust section', outcome: 'Show only credentials you truly hold' },
  { id: 'contact_section', title: 'Improve my contact section', outcome: 'Make the next step obvious on mobile' },
  { id: 'technical_seo', title: 'Technical SEO fixes', outcome: 'Titles, meta, headings, and structured data' },
  { id: 'seo_plan', title: 'My SEO action plan', outcome: 'This week / Next / Later checklist' },
];

function statusLabel(s: string) {
  if (s === 'done') return 'Done';
  if (s === 'copied') return 'Copied';
  if (s === 'draft') return 'Draft ready';
  return 'Not started';
}

function downloadText(filename: string, text: string, mime: string) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const textareaClass =
  'w-full rounded-lg border border-border-strong bg-bg-surface px-3 py-2 text-sm text-text-primary shadow-xs placeholder:text-text-muted focus-visible:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/30';

export function ImprovementsPage() {
  const search = useSearch({ strict: false }) as {
    findingId?: string;
    reviewId?: string;
    action?: string;
  };

  const [step, setStep] = useState<WizardStep>('home');
  const [home, setHome] = useState<HomeData | null>(null);
  const [facts, setFacts] = useState<BusinessFacts | null>(null);
  const [saveToProfile, setSaveToProfile] = useState(true);
  const [action, setAction] = useState<ImprovementAction | null>(null);
  const [findingId, setFindingId] = useState<string | null>(null);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [detail, setDetail] = useState<OutputDetail | null>(null);
  const [plainText, setPlainText] = useState('');
  const [html, setHtml] = useState('');
  const [previewMode, setPreviewMode] = useState<'phone' | 'desktop'>('phone');
  const [platform, setPlatform] = useState<WebsitePlatform>('wordpress');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [earlyOpen, setEarlyOpen] = useState(false);
  const [earlyRequested, setEarlyRequested] = useState(false);
  const [earlyForm, setEarlyForm] = useState({
    interests: [] as string[],
    platform: '',
    leadsPerMonth: '',
    capabilitySuggestion: '',
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3200);
  };

  const loadHome = useCallback(async () => {
    try {
      const data = await api<HomeData>('/api/v1/improvements/home');
      setHome(data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load Fix kit');
    }
  }, []);

  const loadFacts = useCallback(async () => {
    const data = await api<{ facts: BusinessFacts }>('/api/v1/improvements/facts');
    setFacts(data.facts);
    return data.facts;
  }, []);

  const loadEarly = useCallback(async () => {
    try {
      const data = await api<{ request: { withdrawnAt?: string | null } | null }>(
        '/api/v1/improvements/early-access/website_insights',
      );
      setEarlyRequested(Boolean(data.request && !data.request.withdrawnAt));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void loadHome();
    void loadEarly();
  }, [loadHome, loadEarly]);

  // Deep link from Fix this / Fix kit
  useEffect(() => {
    if (!search?.findingId && !search?.action) return;
    const act = (search.action as ImprovementAction) || null;
    setFindingId(search.findingId ?? null);
    setReviewId(search.reviewId ?? null);
    if (act && ACTIONS.some((a) => a.id === act)) {
      setAction(act);
    } else if (search.findingId) {
      // Resolve action from home recommended or default service_page after home loads
      setAction(null);
    }
    void (async () => {
      await loadFacts();
      setStep('facts');
    })();
  }, [search?.findingId, search?.action, search?.reviewId, loadFacts]);

  useEffect(() => {
    if (step !== 'facts' || action || !home || !findingId) return;
    const rec = home.recommended.find((r) => r.findingId === findingId);
    if (rec) setAction(rec.action);
    else setAction('service_page');
  }, [step, action, home, findingId]);

  const startAction = async (a: ImprovementAction, fId?: string | null) => {
    setError(null);
    setAction(a);
    setFindingId(fId ?? null);
    setReviewId(search?.reviewId ?? home?.latestReviewId ?? null);
    setBusy(true);
    try {
      await loadFacts();
      setStep('facts');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load facts');
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    if (!action || !facts) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<OutputDetail>('/api/v1/improvements/generate', {
        method: 'POST',
        body: JSON.stringify({
          actionType: action,
          facts,
          findingId,
          reviewId,
          saveToProfile,
          outputId: detail?.output.id ?? null,
        }),
      });
      setDetail(res);
      setPlainText(res.output.content.plainText ?? '');
      setHtml(res.output.html ?? '');
      setStep('edit');
      void loadHome();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not generate draft');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const res = await api<OutputDetail>(`/api/v1/improvements/outputs/${detail.output.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ plainText, html }),
      });
      setDetail(res);
      showToast('Draft saved');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const resetSuggested = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const res = await api<OutputDetail>(`/api/v1/improvements/outputs/${detail.output.id}/reset`, {
        method: 'POST',
      });
      setDetail(res);
      setPlainText(res.output.content.plainText ?? '');
      setHtml(res.output.html ?? '');
      showToast('Reset to suggested');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reset');
    } finally {
      setBusy(false);
    }
  };

  const markCopied = async () => {
    if (!detail) return;
    await api(`/api/v1/improvements/outputs/${detail.output.id}/copied`, { method: 'POST' });
    void loadHome();
  };

  const markDone = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await api(`/api/v1/improvements/outputs/${detail.output.id}/done`, {
        method: 'POST',
        body: JSON.stringify({ markFindingDone: true }),
      });
      showToast(STRINGS.improvement.markDone);
      setStep('home');
      setDetail(null);
      void loadHome();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not mark done');
    } finally {
      setBusy(false);
    }
  };

  const copyText = async () => {
    await navigator.clipboard.writeText(plainText);
    await markCopied();
    if (detail && detail.output.placeholdersOpen > 0) {
      showToast(`${detail.output.placeholdersOpen} items still need your input — copied anyway`);
    } else {
      showToast('Copied text');
    }
  };

  const copyHtml = async () => {
    await navigator.clipboard.writeText(html);
    await markCopied();
    if (detail && detail.output.placeholdersOpen > 0) {
      showToast(`${detail.output.placeholdersOpen} items still need your input — copied anyway`);
    } else {
      showToast('Copied HTML');
    }
  };

  const emailWebPerson = async () => {
    if (!detail) return;
    const res = await api<{ mailto: string; warning: string | null }>(
      `/api/v1/improvements/outputs/${detail.output.id}/email-draft`,
      { method: 'POST' },
    );
    if (res.warning) showToast(res.warning);
    window.location.href = res.mailto;
  };

  const submitEarlyAccess = async (withdraw = false) => {
    setBusy(true);
    try {
      await api('/api/v1/improvements/early-access/website_insights', {
        method: 'POST',
        body: JSON.stringify({ ...earlyForm, withdraw }),
      });
      setEarlyRequested(!withdraw);
      setEarlyOpen(false);
      showToast(withdraw ? 'Interest withdrawn' : "You're on the list.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save request');
    } finally {
      setBusy(false);
    }
  };

  const placeholdersOpen = useMemo(() => {
    const re = /\[Add[^\]]*\]/gi;
    const found = `${plainText}\n${html}`.match(re) ?? [];
    return new Set(found.map((p) => p.trim())).size;
  }, [plainText, html]);

  const aiOn = aiImprovementEnabledClient() || home?.aiEnabled;

  if (step === 'facts' && facts && action) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
          onClick={() => {
            setStep('home');
            setDetail(null);
          }}
        >
          <ArrowLeft className="h-4 w-4" /> Back to Fix kit
        </button>
        <PageHeader
          title="Your facts"
          description={ACTIONS.find((a) => a.id === action)?.title}
        />
        <p className="rounded-lg border border-border bg-bg-muted/40 px-3 py-2 text-sm text-text-secondary">
          {STRINGS.improvement.factsBanner}
        </p>
        {error && <p className="text-sm text-danger">{error}</p>}
        <FactsForm facts={facts} onChange={setFacts} action={action} />
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={saveToProfile}
            onChange={(e) => setSaveToProfile(e.target.checked)}
          />
          Save to my profile
        </label>
        {!aiOn && (
          <p className="text-xs text-text-muted">Drafts use templates only — AI wording help is off.</p>
        )}
        <Button onClick={() => void generate()} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Generate draft
        </Button>
      </div>
    );
  }

  if (step === 'edit' && detail) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
            onClick={() => setStep('facts')}
          >
            <ArrowLeft className="h-4 w-4" /> Facts
          </button>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => void resetSuggested()} disabled={busy}>
              <RotateCcw className="h-3.5 w-3.5" /> Reset to suggested
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void saveEdit()} disabled={busy}>
              Save
            </Button>
            <Button size="sm" onClick={() => setStep('export')}>
              Export
            </Button>
          </div>
        </div>
        <PageHeader
          title={detail.output.content.title || 'Draft'}
          description={`Revision ${detail.output.version} · last 5 kept`}
        />
        {placeholdersOpen > 0 && (
          <p className="rounded-lg border border-warning/40 bg-warning-subtle px-3 py-2 text-sm text-text-primary">
            {placeholdersOpen} item{placeholdersOpen === 1 ? '' : 's'} still need your input ([Add …]
            highlighted in preview).
          </p>
        )}
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-text-primary">Edit text</label>
            <textarea
              className={cn(textareaClass, 'min-h-[28rem] font-mono text-[13px]')}
              value={plainText}
              onChange={(e) => setPlainText(e.target.value)}
            />
            {detail.revisions.length > 0 && (
              <div className="text-xs text-text-muted">
                History:{' '}
                {detail.revisions
                  .map((r) => `v${r.revision}`)
                  .join(' · ')}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-text-primary">Preview</label>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant={previewMode === 'phone' ? 'primary' : 'secondary'}
                  onClick={() => setPreviewMode('phone')}
                >
                  <Phone className="h-3.5 w-3.5" /> Phone
                </Button>
                <Button
                  size="sm"
                  variant={previewMode === 'desktop' ? 'primary' : 'secondary'}
                  onClick={() => setPreviewMode('desktop')}
                >
                  <Monitor className="h-3.5 w-3.5" /> Desktop
                </Button>
              </div>
            </div>
            <div
              className={cn(
                'mx-auto overflow-hidden rounded-xl border border-border bg-white shadow-card',
                previewMode === 'phone' ? 'max-w-[390px]' : 'w-full',
              )}
            >
              <iframe
                title="Draft preview"
                className="h-[28rem] w-full bg-white"
                sandbox=""
                srcDoc={html}
              />
            </div>
          </div>
        </div>
        {toast && <Toast msg={toast} />}
      </div>
    );
  }

  if (step === 'export' && detail) {
    const guide = PLATFORM_GUIDES.find((g) => g.id === platform) ?? PLATFORM_GUIDES[0];
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
          onClick={() => setStep('edit')}
        >
          <ArrowLeft className="h-4 w-4" /> Back to editor
        </button>
        <PageHeader title="Export and place on your site" description="We never overwrite your live website." />
        {placeholdersOpen > 0 && (
          <p className="rounded-lg border border-warning/40 bg-warning-subtle px-3 py-2 text-sm">
            {placeholdersOpen} items still need your input — export is allowed with this warning.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => void copyText()}>
            <Copy className="h-3.5 w-3.5" /> Copy text
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void copyHtml()}>
            <Copy className="h-3.5 w-3.5" /> Copy HTML
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              downloadText('improvement.html', html, 'text/html');
              void markCopied();
            }}
          >
            <Download className="h-3.5 w-3.5" /> .html
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              downloadText('improvement.txt', plainText, 'text/plain');
              void markCopied();
            }}
          >
            <FileText className="h-3.5 w-3.5" /> .txt
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              // Lightweight .docx-compatible HTML package for web developers
              const doc = `<html xmlns:o="urn:schemas-microsoft-com:office:office"><head><meta charset="utf-8"></head><body><pre>${plainText
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')}</pre></body></html>`;
              downloadText('improvement.docx', doc, 'application/msword');
              void markCopied();
            }}
          >
            <FileText className="h-3.5 w-3.5" /> .docx
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void emailWebPerson()}>
            <Mail className="h-3.5 w-3.5" /> Email my web person
          </Button>
        </div>

        {action === 'seo_plan' && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              const w = window.open('', '_blank');
              if (!w) return;
              w.document.write(html);
              w.document.close();
              w.focus();
              w.print();
            }}
          >
            Print one-page PDF
          </Button>
        )}

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-text-primary">How to add it to your website</h3>
          <Select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as WebsitePlatform)}
          >
            {PLATFORM_GUIDES.map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </Select>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-text-secondary">
            {guide.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
          <p className="text-sm text-text-muted">
            Prefer not to edit the site yourself? Use “Email my web person” with the draft.
          </p>
        </section>

        <div className="flex flex-wrap gap-2 border-t border-border pt-4">
          <Button onClick={() => void markDone()} disabled={busy}>
            <Check className="h-4 w-4" /> {STRINGS.improvement.markDone}
          </Button>
          {reviewId && (
            <a href={`/app/review/${reviewId}`}>
              <Button variant="secondary">{STRINGS.improvement.recheckOffer}</Button>
            </a>
          )}
        </div>
        {toast && <Toast msg={toast} />}
      </div>
    );
  }

  // Kit home
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title={STRINGS.improvement.sectionTitle}
        description={STRINGS.improvement.sectionSubtitle}
      />
      {error && (
        <p className="rounded-lg border border-danger/30 bg-danger-subtle px-3 py-2 text-sm text-danger">{error}</p>
      )}

      {home?.recommended && home.recommended.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-text-primary">Recommended for your website</h2>
          <div className="mt-3 space-y-2">
            {home.recommended.map((r) => (
              <button
                key={r.findingId}
                type="button"
                onClick={() => void startAction(r.action, r.findingId)}
                className="flex w-full items-start justify-between gap-3 rounded-xl border border-border bg-bg-surface p-4 text-left shadow-card hover:border-brand/30"
              >
                <div>
                  <p className="font-medium text-text-primary">{r.title}</p>
                  {r.why && <p className="mt-1 text-sm text-text-secondary">{r.why}</p>}
                </div>
                <span className="shrink-0 text-sm font-medium text-brand">Fix this</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-sm font-semibold text-text-primary">Create an improvement</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {ACTIONS.map((a) => (
            <button
              key={a.id}
              type="button"
              disabled={busy}
              onClick={() => void startAction(a.id, findingId)}
              className="rounded-xl border border-border bg-bg-surface p-4 text-left shadow-card transition hover:border-brand/30"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-subtle text-brand">
                  <Wrench className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-text-primary">{a.title}</p>
                  <p className="mt-1 text-sm text-text-secondary">{a.outcome}</p>
                  <p className="mt-2 text-xs text-text-muted">
                    {home?.findingCounts?.[a.id] ?? 0} related finding
                    {(home?.findingCounts?.[a.id] ?? 0) === 1 ? '' : 's'} ·{' '}
                    {statusLabel(home?.statusByAction?.[a.id] ?? 'not_started')}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {home?.outputs && home.outputs.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-text-primary">Recent drafts</h2>
          <ul className="mt-2 space-y-1 text-sm text-text-secondary">
            {home.outputs.slice(0, 5).map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  className="text-left hover:text-brand"
                  onClick={async () => {
                    const res = await api<OutputDetail>(`/api/v1/improvements/outputs/${o.id}`);
                    setDetail(res);
                    setAction(o.actionType as ImprovementAction);
                    setPlainText(res.output.content.plainText ?? '');
                    setHtml(res.output.html ?? '');
                    setFacts(res.output.factsSnapshot);
                    setStep('edit');
                  }}
                >
                  {ACTIONS.find((a) => a.id === o.actionType)?.title ?? o.actionType} · v{o.version} ·{' '}
                  {statusLabel(o.status)}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <ComingSoonCard
        requested={earlyRequested}
        open={earlyOpen}
        onOpen={() => setEarlyOpen(true)}
        onClose={() => setEarlyOpen(false)}
        form={earlyForm}
        setForm={setEarlyForm}
        onSubmit={() => void submitEarlyAccess(false)}
        onWithdraw={() => void submitEarlyAccess(true)}
        busy={busy}
      />

      {toast && <Toast msg={toast} />}
    </div>
  );
}

function Toast({ msg }: { msg: string }) {
  return (
    <div className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-text-primary px-4 py-2 text-sm text-white shadow-raised md:bottom-6">
      {msg}
    </div>
  );
}

function FactsForm({
  facts,
  onChange,
  action,
}: {
  facts: BusinessFacts;
  onChange: (f: BusinessFacts) => void;
  action: ImprovementAction;
}) {
  const set = <K extends keyof BusinessFacts>(key: K, value: BusinessFacts[K]) =>
    onChange({ ...facts, [key]: value });

  return (
    <div className="space-y-4 rounded-xl border border-border bg-bg-surface p-4">
      <Field label="Business name">
        <Input value={facts.businessName} onChange={(e) => set('businessName', e.target.value)} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Phone">
          <Input value={facts.phone} onChange={(e) => set('phone', e.target.value)} />
        </Field>
        <Field label="Hours">
          <Input value={facts.hours} onChange={(e) => set('hours', e.target.value)} placeholder="e.g. Mon–Fri 8–5" />
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="City">
          <Input value={facts.city} onChange={(e) => set('city', e.target.value)} />
        </Field>
        <Field label="State">
          <Input value={facts.state} onChange={(e) => set('state', e.target.value)} />
        </Field>
      </div>
      <Field label="Services (comma-separated)">
        <Input
          value={facts.services.join(', ')}
          onChange={(e) =>
            set(
              'services',
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
        />
      </Field>
      {(action === 'service_page' || action === 'technical_seo') && (
        <Field label="Service for this draft">
          <Input
            value={facts.selectedService}
            onChange={(e) => set('selectedService', e.target.value)}
            list="svc-list"
          />
          <datalist id="svc-list">
            {facts.services.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </Field>
      )}
      <Field label="Service area (comma-separated)">
        <Input
          value={facts.serviceArea.join(', ')}
          onChange={(e) =>
            set(
              'serviceArea',
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
        />
      </Field>
      <Field label="Differentiators (one per line)">
        <textarea
          className={textareaClass}
          value={facts.differentiators.join('\n')}
          onChange={(e) =>
            set(
              'differentiators',
              e.target.value
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          rows={3}
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="License number (optional)">
          <Input value={facts.licenseNumber} onChange={(e) => set('licenseNumber', e.target.value)} />
        </Field>
        <Field label="Years in business (optional)">
          <Input
            type="number"
            min={1}
            value={facts.yearsInBusiness ?? ''}
            onChange={(e) =>
              set('yearsInBusiness', e.target.value ? Number(e.target.value) : null)
            }
          />
        </Field>
      </div>
      <Field label="Warranty (optional)">
        <Input value={facts.warranty} onChange={(e) => set('warranty', e.target.value)} />
      </Field>

      {action === 'trust_section' && (
        <div className="space-y-2 border-t border-border pt-3">
          <p className="text-sm font-medium">Proof you truly have</p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={facts.insured}
              onChange={(e) => set('insured', e.target.checked)}
            />
            Insured
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={facts.bonded}
              onChange={(e) => set('bonded', e.target.checked)}
            />
            Bonded
          </label>
          <Field label="Layout">
            <Select
              value={facts.trustLayout}
              onChange={(e) => set('trustLayout', e.target.value as BusinessFacts['trustLayout'])}
            >
              <option value="badges">Row of badges</option>
              <option value="grid">Why trust us grid</option>
              <option value="proof_strip">Proof strip</option>
            </Select>
          </Field>
          <Field label="Review links (one per line)">
            <textarea
              className={textareaClass}
              value={facts.reviewLinks.join('\n')}
              onChange={(e) =>
                set(
                  'reviewLinks',
                  e.target.value
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
                )
              }
              rows={2}
            />
          </Field>
        </div>
      )}

      {action === 'contact_section' && (
        <div className="space-y-2 border-t border-border pt-3">
          <Field label="Preferred contact">
            <Select
              value={facts.preferredContact}
              onChange={(e) => set('preferredContact', e.target.value as BusinessFacts['preferredContact'])}
            >
              <option value="call">Call</option>
              <option value="text">Text</option>
              <option value="form">Form</option>
            </Select>
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={facts.canReceiveSms}
              onChange={(e) => set('canReceiveSms', e.target.checked)}
            />
            This number can receive texts (required for SMS buttons)
          </label>
          <Field label="Response promise (optional)">
            <Input
              value={facts.responsePromise}
              onChange={(e) => set('responsePromise', e.target.value)}
              placeholder="We reply within one business day"
            />
          </Field>
          <Field label="Emergency availability (optional)">
            <Input
              value={facts.emergencyAvailability}
              onChange={(e) => set('emergencyAvailability', e.target.value)}
            />
          </Field>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-text-primary">{label}</span>
      {children}
    </label>
  );
}

function ComingSoonCard({
  requested,
  open,
  onOpen,
  onClose,
  form,
  setForm,
  onSubmit,
  onWithdraw,
  busy,
}: {
  requested: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  form: {
    interests: string[];
    platform: string;
    leadsPerMonth: string;
    capabilitySuggestion: string;
  };
  setForm: (f: typeof form) => void;
  onSubmit: () => void;
  onWithdraw: () => void;
  busy: boolean;
}) {
  const interests = [
    { id: 'gsc_ga', label: 'Search Console and Analytics' },
    { id: 'suggestions', label: 'Intelligent suggestions' },
    { id: 'ai_content', label: 'AI content help' },
  ];

  return (
    <section className="rounded-xl border border-dashed border-border bg-bg-muted/30 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            {STRINGS.improvement.comingSoonBadge}
          </p>
          <h3 className="mt-1 font-semibold text-text-primary">{STRINGS.improvement.comingSoonTitle}</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-text-secondary">
            <li>Connect Google Search Console and Analytics (read-only) to see what customers search and click</li>
            <li>Intelligent suggestions based on your own data</li>
            <li>AI help writing service pages and descriptions you can edit</li>
          </ul>
        </div>
        <Sparkles className="h-5 w-5 shrink-0 text-text-muted" />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {requested ? (
          <>
            <span className="rounded-md bg-bg-subtle px-2 py-1 text-xs text-text-secondary">Requested</span>
            <Button size="sm" variant="secondary" onClick={onWithdraw} disabled={busy}>
              Withdraw interest
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={onOpen}>
            Join the pilot (early access)
          </Button>
        )}
        <Button size="sm" variant="secondary" onClick={onOpen}>
          Suggest a capability
        </Button>
      </div>

      {open && (
        <div className="mt-4 space-y-3 rounded-lg border border-border bg-bg-surface p-4">
          <p className="text-sm text-text-secondary">
            Planned data use: read-only connections you authorize later, and optional AI drafting you always edit
            before publishing. Not live yet — no Google passwords or OAuth in this form.
          </p>
          {interests.map((i) => (
            <label key={i.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.interests.includes(i.id)}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...form.interests, i.id]
                    : form.interests.filter((x) => x !== i.id);
                  setForm({ ...form, interests: next });
                }}
              />
              {i.label}
            </label>
          ))}
          <Field label="Website platform">
            <Select
              value={form.platform}
              onChange={(e) => setForm({ ...form, platform: e.target.value })}
            >
              <option value="">Select…</option>
              {PLATFORM_GUIDES.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="How many leads do you get a month? (optional)">
            <Select
              value={form.leadsPerMonth}
              onChange={(e) => setForm({ ...form, leadsPerMonth: e.target.value })}
            >
              <option value="">Prefer not to say</option>
              <option value="0-5">0–5</option>
              <option value="6-20">6–20</option>
              <option value="21-50">21–50</option>
              <option value="50+">50+</option>
            </Select>
          </Field>
          <Field label="Suggest a capability (optional)">
            <textarea
              className={textareaClass}
              value={form.capabilitySuggestion}
              onChange={(e) => setForm({ ...form, capabilitySuggestion: e.target.value })}
              rows={2}
            />
          </Field>
          <div className="flex gap-2">
            <Button size="sm" onClick={onSubmit} disabled={busy}>
              Submit
            </Button>
            <Button size="sm" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
